import { useCallback, useRef } from 'react';
import { SMALL_NAV_TOASTER_ID } from '../tools/constants';

/**
 * Returns handlers to adjust mobile nav toaster.
 * @param {String} [brandFamily=''] - Current brand family
 * @param {Boolean} [hasRefreshedNav=false] - Switch value for refreshed nav
 * @param {Element|null} [navBarElement=null] - Navbar element from ref
 * @returns {{ onOpen: Function }} - An object containing the `onOpen` function.
 *   The function sets up and cleans up the toaster position adjustment logic.
 */
const useUpdateToasterPosition = ({
  brandFamily = '',
  hasRefreshedNav = false,
  navBarElement = null,
} = {}) => {
  // Refs to track observer and handler for cleanup
  const observerRef = useRef(null);
  const mutationTimeoutRef = useRef(null);

  /**
   * Sets up the toaster position updating mechanism when the toaster opens
   * @returns {Function} Cleanup function to remove observers and event handlers
   */
  const onOpen = useCallback(() => {
    // Early return if conditions aren't met for positioning
    if (!hasRefreshedNav || brandFamily === 'hol') return null;

    let hadAppBanner = document.body.classList.contains('branch-banner-is-active');
    const appBannerHeight = parseInt(window.getComputedStyle(document.body).marginTop, 10) || 0;
    const smallScreenNavElement = document.getElementById(SMALL_NAV_TOASTER_ID);

    // If we can't find the toaster element, don't proceed
    if (!smallScreenNavElement) return null;

    /**
     * Updates toaster position based on navbar position and app banner state
     */
    const updateToasterPosition = () => {
      const hasAppBanner = document.body.classList.contains('branch-banner-is-active');
      const appBannerRemoved = hadAppBanner && !hasAppBanner;

      if (navBarElement && smallScreenNavElement) {
        // Batch all DOM reads together
        const navBarRect = navBarElement.getBoundingClientRect();
        const navBarTop = navBarRect.top;

        // Then batch all DOM writes
        if (appBannerRemoved) {
          requestAnimationFrame(() => {
            const newTopToasterPosition = navBarTop <= 0
              ? 0
              : navBarTop + window.scrollY;
            smallScreenNavElement.style.top = `${Math.max(0, newTopToasterPosition - appBannerHeight)}px`;
          });
        } else {
          smallScreenNavElement.style.top = `${Math.max(0, navBarTop)}px`;
        }
      }

      // Update state after DOM operations
      hadAppBanner = hasAppBanner;
    };

    // Run once on initial effect to set position
    updateToasterPosition();

    // Set up Mutation Observer to detect app banner changes
    observerRef.current = new MutationObserver((mutations) => {
      const hasClassChanges = mutations.some(
        (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'class',
      );

      if (hasClassChanges) {
        cancelAnimationFrame(mutationTimeoutRef.current);
        mutationTimeoutRef.current = requestAnimationFrame(updateToasterPosition);
      }
    });

    // Start observing body class changes
    observerRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });

    // Return cleanup function
    return () => {
      // Clean up all observers and animation frames
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mutationTimeoutRef.current) {
        cancelAnimationFrame(mutationTimeoutRef.current);
        mutationTimeoutRef.current = null;
      }
    };
  }, [navBarElement, hasRefreshedNav, brandFamily]);

  return { onOpen };
};

export default useUpdateToasterPosition;
